import React, { useCallback, useEffect, useState } from "react";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, btnDanger, inputStyle } from "../theme";
import { api } from "../api/client";
import { fmtDate } from "../utils/format";

// ── Certification body connectors — bureau admin view ──────────────────────
// Outbound, signed push of audit events (schedule, findings, evidence
// packages, sign-off) into the certification body's own platform.

interface Connector {
  id: string;
  name: string;
  platform: string;
  endpointUrl: string;
  events: string[];
  active: boolean;
  hasAuthHeader: boolean;
  lastDeliveredAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
}

interface Delivery {
  id: string;
  event: string;
  attempt: number;
  statusCode: number | null;
  success: boolean;
  error: string | null;
  durationMs: number | null;
  createdAt: string;
  organizationId: string | null;
}

const PLATFORM_LABELS: Record<string, string> = {
  generic: "Generic (signed JSON)",
  bsi:     "BSI",
  dnv:     "DNV",
  lrqa:    "LRQA",
  sgs:     "SGS",
  nqa:     "NQA",
  other:   "Other",
};

const EVENT_LABELS: Record<string, string> = {
  "audit.scheduled":           "Audit scheduled / rescheduled",
  "audit.completed":           "Formal audit sign-off",
  "evidence_package.exported": "Evidence package exported (includes 10-min download link)",
  "finding.created":           "Finding recorded",
  "finding.updated":           "Finding updated",
  "evidence_request.created":  "Evidence request raised",
};

const emptyForm = { name: "", platform: "generic", endpointUrl: "", authHeader: "", events: [] as string[] };

export function BureauConnectorsView() {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [events, setEvents]         = useState<string[]>([]);
  const [platforms, setPlatforms]   = useState<string[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(emptyForm);
  const [saving, setSaving]         = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<{ id: string; secret: string } | null>(null);
  // The connector the form is editing (PATCH), or null when it creates one. A blank
  // auth header keeps the stored one; clearAuth removes it.
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editingHasAuth, setEditingHasAuth] = useState(false);
  const [clearAuth, setClearAuth]   = useState(false);

  const [busy, setBusy]             = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { success: boolean; statusCode: number | null; error: string | null }>>({});
  const [deliveries, setDeliveries] = useState<Record<string, Delivery[]>>({});
  const [openLog, setOpenLog]       = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ connectors: Connector[]; events: string[]; platforms: string[] }>("/auditor/bureau/connectors");
      setConnectors(d.connectors);
      setEvents(d.events);
      setPlatforms(d.platforms);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load connectors. Bureau admin access required.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEvent = (ev: string) =>
    setForm((f) => ({ ...f, events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev] }));

  function startEdit(c: Connector) {
    setEditingId(c.id);
    setEditingHasAuth(c.hasAuthHeader);
    setClearAuth(false);
    setForm({ name: c.name, platform: c.platform, endpointUrl: c.endpointUrl, authHeader: "", events: [...c.events] });
    setShowForm(true);
    setRevealedSecret(null);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setClearAuth(false);
    setForm(emptyForm);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (editingId) return handleUpdate();
    setSaving(true);
    setError(null);
    try {
      const d = await api.post<{ connector: Connector; signingSecret: string }>("/auditor/bureau/connectors", {
        name: form.name.trim(),
        platform: form.platform,
        endpointUrl: form.endpointUrl.trim(),
        ...(form.authHeader.trim() ? { authHeader: form.authHeader.trim() } : {}),
        events: form.events,
      });
      setRevealedSecret({ id: d.connector.id, secret: d.signingSecret });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create connector");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!editingId) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/auditor/bureau/connectors/${editingId}`, {
        name: form.name.trim(),
        platform: form.platform,
        endpointUrl: form.endpointUrl.trim(),
        events: form.events,
        ...(form.authHeader.trim() ? { authHeader: form.authHeader.trim() } : clearAuth ? { authHeader: null } : {}),
      });
      closeForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update connector");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(c: Connector) {
    setBusy(c.id);
    try {
      await api.patch(`/auditor/bureau/connectors/${c.id}`, { active: !c.active });
      setConnectors((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: !c.active } : x)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleTest(c: Connector) {
    setBusy(c.id);
    try {
      const r = await api.post<{ success: boolean; statusCode: number | null; error: string | null }>(`/auditor/bureau/connectors/${c.id}/test`, {});
      setTestResult((prev) => ({ ...prev, [c.id]: r }));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleRotate(c: Connector) {
    if (!confirm(`Rotate the signing secret for "${c.name}"? The current secret stops working immediately.`)) return;
    setBusy(c.id);
    try {
      const r = await api.post<{ signingSecret: string }>(`/auditor/bureau/connectors/${c.id}/rotate-secret`, {});
      setRevealedSecret({ id: c.id, secret: r.signingSecret });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rotation failed");
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(c: Connector) {
    if (!confirm(`Delete connector "${c.name}"? Queued deliveries to it will be dropped.`)) return;
    setBusy(c.id);
    try {
      await api.del(`/auditor/bureau/connectors/${c.id}`);
      setConnectors((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggleLog(c: Connector) {
    if (openLog === c.id) { setOpenLog(null); return; }
    setOpenLog(c.id);
    if (!deliveries[c.id]) {
      try {
        const d = await api.get<{ deliveries: Delivery[] }>(`/auditor/bureau/connectors/${c.id}/deliveries`);
        setDeliveries((prev) => ({ ...prev, [c.id]: d.deliveries }));
      } catch {
        setDeliveries((prev) => ({ ...prev, [c.id]: [] }));
      }
    }
  }

  const statusChip = (c: Connector) => {
    if (!c.active) return { label: "Paused", bg: theme.color.panelAlt, fg: theme.color.subtle };
    if (c.lastStatusCode == null) return { label: "Not yet delivered", bg: theme.color.panelAlt, fg: theme.color.subtle };
    if (c.lastStatusCode >= 200 && c.lastStatusCode < 300) return { label: `OK (${c.lastStatusCode})`, bg: theme.color.successBg, fg: theme.color.successText };
    return { label: `Failing (${c.lastStatusCode})`, bg: theme.color.dangerBg, fg: theme.color.dangerText };
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: theme.color.heading }}>Certification Body Integrations</div>
          <div style={{ fontSize: 13, color: theme.color.muted, marginTop: 4, maxWidth: 720 }}>
            Push audit events from this bureau directly into your certification body&apos;s platform. Every delivery is a signed JSON
            POST over HTTPS (<code>X-Abacas-Signature-V2</code>, HMAC-SHA256 over <code>timestamp.body</code>). Only events for
            organisations assigned to this bureau are ever sent, and only when one of your auditors takes the action.
          </div>
        </div>
        {!showForm && (
          <button style={btnPrimary} onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true); setRevealedSecret(null); }}>+ Add connector</button>
        )}
      </div>

      {error && <div style={{ fontSize: 13, color: theme.color.dangerText, background: theme.color.dangerBg, padding: "10px 14px", borderRadius: 3 }}>{error}</div>}

      {revealedSecret && (
        <div style={{ background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: 3, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.warningText, marginBottom: 6 }}>Signing secret — shown once</div>
          <div style={{ fontSize: 12, color: theme.color.warningText, marginBottom: 8 }}>
            Give this to the certification body so their system can verify deliveries. It will not be shown again; rotate it if lost.
          </div>
          <code style={{ display: "block", fontSize: 12, padding: "8px 10px", background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, wordBreak: "break-all", userSelect: "all" }}>
            {revealedSecret.secret}
          </code>
          <button style={{ ...btnSecondary, marginTop: 10 }} onClick={() => setRevealedSecret(null)}>I have saved it</button>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} style={{ ...card, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label style={{ fontSize: 12, color: theme.color.muted }}>
              Name
              <input style={{ ...inputStyle, marginTop: 4 }} required maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. BSI Connect" />
            </label>
            <label style={{ fontSize: 12, color: theme.color.muted }}>
              Platform
              <select style={{ ...inputStyle, marginTop: 4 }} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
                {platforms.map((p) => <option key={p} value={p}>{PLATFORM_LABELS[p] ?? p}</option>)}
              </select>
            </label>
          </div>
          <label style={{ fontSize: 12, color: theme.color.muted }}>
            Endpoint URL (HTTPS)
            <input style={{ ...inputStyle, marginTop: 4 }} required type="url" pattern="https://.*" value={form.endpointUrl} onChange={(e) => setForm({ ...form, endpointUrl: e.target.value })} placeholder="https://api.certbody.example/abacas/events" />
          </label>
          <label style={{ fontSize: 12, color: theme.color.muted }}>
            {editingId && editingHasAuth
              ? <>Authorization header value (a header is stored; leave blank to keep it, or enter a new one to replace it)</>
              : <>Authorization header value (optional — e.g. <code>Bearer …</code>; stored encrypted, never shown again)</>}
            <input style={{ ...inputStyle, marginTop: 4 }} type="password" autoComplete="off" maxLength={4096} value={form.authHeader} disabled={clearAuth} onChange={(e) => setForm({ ...form, authHeader: e.target.value })} />
          </label>
          {editingId && editingHasAuth && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: theme.color.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={clearAuth} onChange={(e) => { setClearAuth(e.target.checked); if (e.target.checked) setForm({ ...form, authHeader: "" }); }} />
              Remove the stored authorization header
            </label>
          )}
          <div>
            <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 6 }}>Events to send</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {events.map((ev) => (
                <label key={ev} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: theme.color.heading, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.events.includes(ev)} onChange={() => toggleEvent(ev)} />
                  <span><code style={{ fontSize: 12 }}>{ev}</code> — {EVENT_LABELS[ev] ?? ev}</span>
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btnPrimary} disabled={saving || form.events.length === 0}>{saving ? "Saving…" : editingId ? "Save changes" : "Create connector"}</button>
            <button type="button" style={btnSecondary} onClick={closeForm}>Cancel</button>
          </div>
        </form>
      )}

      {loading && <div style={{ fontSize: 13, color: theme.color.muted }}>Loading…</div>}

      {!loading && connectors.length === 0 && !showForm && (
        <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.subtle, padding: "24px 20px" }}>
          No connectors yet. Add one to start pushing audit events to your certification body.
        </div>
      )}

      {connectors.map((c) => {
        const chip = statusChip(c);
        const t = testResult[c.id];
        const isBusy = busy === c.id;
        return (
          <div key={c.id} style={{ ...card, padding: "16px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>
                  {c.name}
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: theme.color.accentBg, color: theme.color.accentText }}>
                    {PLATFORM_LABELS[c.platform] ?? c.platform}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: theme.color.muted, wordBreak: "break-all" }}>{c.endpointUrl}</div>
              </div>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 3, background: chip.bg, color: chip.fg, fontWeight: 600 }}>{chip.label}</span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
              {c.events.map((ev) => (
                <span key={ev} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.subtle }}>{ev}</span>
              ))}
              {c.hasAuthHeader && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 3, background: theme.color.panelAlt, color: theme.color.subtle }}>auth header set</span>}
            </div>

            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 8 }}>
              {c.lastDeliveredAt ? `Last delivery ${fmtDate(c.lastDeliveredAt)}` : "No deliveries yet"}
              {c.lastError && <span style={{ color: theme.color.dangerText }}> · {c.lastError}</span>}
            </div>

            {t && (
              <div style={{ marginTop: 8, fontSize: 12, color: t.success ? theme.color.successText : theme.color.dangerText }}>
                Test {t.success ? "succeeded" : "failed"}{t.statusCode != null ? ` (HTTP ${t.statusCode})` : ""}{t.error ? ` — ${t.error}` : ""}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button style={btnSecondary} disabled={isBusy} onClick={() => handleTest(c)}>Send test</button>
              <button style={btnSecondary} disabled={isBusy || editingId === c.id} onClick={() => startEdit(c)}>Edit</button>
              <button style={btnSecondary} disabled={isBusy} onClick={() => handleToggleActive(c)}>{c.active ? "Pause" : "Resume"}</button>
              <button style={btnSecondary} disabled={isBusy} onClick={() => handleRotate(c)}>Rotate secret</button>
              <button style={btnSecondary} disabled={isBusy} onClick={() => toggleLog(c)}>{openLog === c.id ? "Hide log" : "Delivery log"}</button>
              <button style={btnDanger} disabled={isBusy} onClick={() => handleDelete(c)}>Delete</button>
            </div>

            {openLog === c.id && (
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                {(deliveries[c.id] ?? []).length === 0 ? (
                  <div style={{ fontSize: 12, color: theme.color.subtle }}>No deliveries recorded.</div>
                ) : (
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: theme.color.muted, textAlign: "left", borderBottom: "1px solid var(--c-line)" }}>
                        <th style={{ padding: "4px 8px" }}>When</th>
                        <th style={{ padding: "4px 8px" }}>Event</th>
                        <th style={{ padding: "4px 8px" }}>Attempt</th>
                        <th style={{ padding: "4px 8px" }}>Status</th>
                        <th style={{ padding: "4px 8px" }}>Time</th>
                        <th style={{ padding: "4px 8px" }}>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(deliveries[c.id] ?? []).map((d) => (
                        <tr key={d.id} style={{ borderTop: `1px solid ${theme.color.border}`, color: theme.color.heading }}>
                          <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>{fmtDate(d.createdAt)}</td>
                          <td style={{ padding: "4px 8px" }}><code>{d.event}</code></td>
                          <td style={{ padding: "4px 8px" }}>{d.attempt}</td>
                          <td style={{ padding: "4px 8px", color: d.success ? theme.color.successText : theme.color.dangerText }}>{d.statusCode ?? "—"}</td>
                          <td style={{ padding: "4px 8px" }}>{d.durationMs != null ? `${d.durationMs} ms` : "—"}</td>
                          <td style={{ padding: "4px 8px", color: theme.color.muted }}>{d.error ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
